/* Proprietary - see LICENSE */
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
// Eagerly load Login (it's the first screen users see)
import Login from './pages/Login';
import './App.css';

// Lazy-load all other pages — they're only fetched when navigated to
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Frameworks = React.lazy(() => import('./pages/Frameworks'));
const AiSystems = React.lazy(() => import('./pages/AiSystems'));
const PublicForm = React.lazy(() => import('./pages/PublicForm'));
const Kartoitus = React.lazy(() => import('./pages/Kartoitus'));
const Tasks = React.lazy(() => import('./pages/Tasks'));
const Risks = React.lazy(() => import('./pages/Risks'));
const Assets = React.lazy(() => import('./pages/Assets'));
const Incidents = React.lazy(() => import('./pages/Incidents'));
const Reports = React.lazy(() => import('./pages/Reports'));
const AIAssistant = React.lazy(() => import('./pages/AIAssistant'));
const Users = React.lazy(() => import('./pages/Users'));
const Documents = React.lazy(() => import('./pages/Documents'));
const Whistleblower = React.lazy(() => import('./pages/Whistleblower'));
const WhistleblowerReport = React.lazy(() => import('./pages/WhistleblowerReport'));
const Settings = React.lazy(() => import('./pages/Settings'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const TenantSelector = React.lazy(() => import('./pages/TenantSelector'));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #1976d2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

/**
 * Customer-facing questionnaire links (/q/:token) and the website's
 * kartoitus (/kartoitus) must render without login and without the app
 * chrome — the token is the only credential, and the kartoitus has none.
 */
function PublicFormGate({ children }) {
  const location = useLocation();
  if (location.pathname.startsWith('/q/') || location.pathname.startsWith('/kartoitus')) {
    return (
      <Routes>
        <Route path="/q/:token" element={<PublicForm />} />
        <Route path="/kartoitus" element={<Kartoitus />} />
      </Routes>
    );
  }
  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    sessionStorage.getItem('token') !== null
  );

  const [selectedTenantId, setSelectedTenantId] = React.useState(
    sessionStorage.getItem('selectedTenantId') || ''
  );

  const currentUser = React.useMemo(() => {
    try {
      const userStr = sessionStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, [isAuthenticated]);

  const requiresTenantSelection = React.useMemo(() => {
    const role = currentUser?.role;
    return ['super_admin', 'owner', 'provider_admin'].includes(role) || currentUser?.multiTenant === true;
  }, [currentUser]);

  // Listen for 401 logout events from apiClient interceptor
  React.useEffect(() => {
    const handleLogout = () => setIsAuthenticated(false);
    const handleTenantSelected = () => {
      setSelectedTenantId(sessionStorage.getItem('selectedTenantId') || '');
    };
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('tenant:selected', handleTenantSelected);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('tenant:selected', handleTenantSelected);
    };
  }, []);

  // Auto-logout after 30 minutes of inactivity
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    let timer = setTimeout(() => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('selectedTenantId');
      setIsAuthenticated(false);
    }, IDLE_TIMEOUT);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('selectedTenantId');
        setIsAuthenticated(false);
      }, IDLE_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setSelectedTenantId('');
      return;
    }
    setSelectedTenantId(sessionStorage.getItem('selectedTenantId') || '');
  }, [isAuthenticated]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <PublicFormGate>
          {!isAuthenticated ? (
            <Routes>
              <Route path="/login" element={<Login setAuth={setIsAuthenticated} />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          ) : (
            <Layout setAuth={setIsAuthenticated}>
              {requiresTenantSelection && !selectedTenantId ? (
                <Routes>
                  <Route path="/select-tenant" element={<TenantSelector />} />
                  <Route path="*" element={<Navigate to="/select-tenant" />} />
                </Routes>
              ) : (
                <Routes>
                  <Route path="/select-tenant" element={<TenantSelector />} />
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/frameworks" element={<Frameworks />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/risks" element={<Risks />} />
                  <Route path="/assets" element={<Assets />} />
                  <Route path="/incidents" element={<Incidents />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/ai-assistant" element={<AIAssistant />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/documents" element={<Documents />} />
                  <Route path="/whistleblower" element={<Whistleblower />} />
                  <Route path="/whistleblower/report" element={<WhistleblowerReport />} />
                  <Route path="/ai-systems" element={<AiSystems />} />
                  <Route path="/settings" element={<Settings setAuth={setIsAuthenticated} />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              )}
            </Layout>
          )}
          </PublicFormGate>
        </Suspense>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
